import { useCallback, useEffect, useMemo, useState } from "react";
import { BarChart3, Info, Users, X } from "lucide-react";
import { Bar, BarChart, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { request } from "@/lib/api";

// Utilised reads as the good outcome and unused as the gap, consistently in
// every chart — a legend that swaps meaning between cards is unreadable.
const GREEN = "#16a34a";
const AMBER = "#f59e0b";
const SLATE = "#94a3b8";
const CATEGORICAL = ["#2563eb", "#7c3aed", "#0891b2", "#db2777", "#65a30d", "#ea580c"];

function titleCase(value) {
  return (value || "").replace(/(^|\s)\S/g, (character) => character.toUpperCase());
}

/** A filter that holds many values, with search — the test list runs to hundreds. */
function MultiSelect({ label, options, selected, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const visible = useMemo(
    () => options.filter((option) => option.label.toLowerCase().includes(search.trim().toLowerCase())),
    [options, search],
  );

  function toggle(value) {
    onChange(selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value]);
  }

  const summary = selected.length === 0 ? label : selected.length === 1
    ? options.find((option) => option.value === selected[0])?.label || `1 selected`
    : `${selected.length} selected`;

  // A dropdown with nothing in it is a dead control. The test catalogue is
  // only populated for clients whose packages have been mapped to tests, so
  // these filters simply do not appear until there is something to pick.
  if (!options.length) return null;

  return (
    <div className="hr-filter">
      <button type="button" className={selected.length ? "hr-filter__trigger is-set" : "hr-filter__trigger"} onClick={() => setOpen((value) => !value)} data-testid={`${testId}-trigger`}>
        <span>{summary}</span>
        {selected.length > 0 && (
          <span
            role="button"
            tabIndex={0}
            aria-label={`Clear ${label}`}
            onClick={(event) => { event.stopPropagation(); onChange([]); setOpen(false); }}
            onKeyDown={(event) => { if (event.key === "Enter") { event.stopPropagation(); onChange([]); setOpen(false); } }}
            data-testid={`${testId}-clear`}
          >
            <X size={14} />
          </span>
        )}
      </button>
      {open && (
        <div className="hr-filter__panel" data-testid={`${testId}-panel`}>
          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search the list" aria-label={`Search ${label}`} />
          <div className="hr-filter__options">
            {visible.map((option) => (
              <label key={option.value}>
                <input type="checkbox" checked={selected.includes(option.value)} onChange={() => toggle(option.value)} />
                <span>{option.label}</span>
              </label>
            ))}
            {!visible.length && <p className="hr-empty">Nothing matches that.</p>}
          </div>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid={`${testId}-done`}>Done</Button>
        </div>
      )}
    </div>
  );
}

function StatCard({ title, value, hint, testId }) {
  return (
    <article className="hr-card hr-card--stat" data-testid={testId}>
      <p className="hr-card__title">{title}</p>
      <strong className="hr-stat">{value}</strong>
      {hint && <small>{hint}</small>}
    </article>
  );
}

function Coverage({ known, population }) {
  if (population === undefined || known === undefined || known >= population) return null;
  return (
    <small className="hr-coverage" title="Percentages are of the people we hold this detail for.">
      Based on {known} of {population} — the rest have nothing on file.
    </small>
  );
}

function DonutCard({ title, distribution, colors, total, testId }) {
  const rows = distribution?.rows || [];
  const data = rows.filter((row) => row.count > 0);
  return (
    <article className="hr-card" data-testid={testId}>
      <p className="hr-card__title">{title}</p>
      {data.length ? (
        <div className="hr-donut">
          <ResponsiveContainer width="100%" height={190}>
            <PieChart>
              <Pie data={data} dataKey="count" nameKey="label" innerRadius={52} outerRadius={78} paddingAngle={2}>
                {data.map((row, index) => <Cell key={row.label} fill={colors[index % colors.length]} />)}
              </Pie>
              <Tooltip formatter={(value, name) => [`${value} (${data.find((row) => row.label === name)?.percentage ?? 0}%)`, titleCase(name)]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="hr-donut__centre"><strong>{total}</strong><span>Total</span></div>
          <ul className="hr-legend">
            {data.map((row, index) => (
              <li key={row.label}>
                <i style={{ background: colors[index % colors.length] }} />
                <span>{titleCase(row.label)}</span>
                <b>{row.percentage}%</b>
              </li>
            ))}
          </ul>
        </div>
      ) : <p className="hr-empty">Nothing recorded yet.</p>}
      <Coverage known={distribution?.known} population={distribution?.population} />
    </article>
  );
}

function AgeCard({ title, distribution, testId }) {
  const rows = distribution?.rows || [];
  return (
    <article className="hr-card" data-testid={testId}>
      <p className="hr-card__title">{title}</p>
      {rows.some((row) => row.count > 0) ? (
        <ResponsiveContainer width="100%" height={190}>
          <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
            <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} />
            <YAxis allowDecimals={false} tickLine={false} axisLine={false} fontSize={12} />
            <Tooltip formatter={(value) => [`${value}`, "People"]} />
            <Bar dataKey="count" fill="#5eb0b7" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      ) : <p className="hr-empty">No dates of birth on file yet.</p>}
      <Coverage known={distribution?.known} population={distribution?.population} />
    </article>
  );
}

export default function HrAnalyticsPage() {
  const { token, user } = useAuth();
  const { name: tenantName } = useTenant();
  const [options, setOptions] = useState({ packages: [], test_categories: [], tests: [] });
  const [packageIds, setPackageIds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [testIds, setTestIds] = useState([]);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    request("/hr/analytics/filters", {}, token).then(setOptions).catch(() => setOptions({ packages: [], test_categories: [], tests: [] }));
  }, [token]);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    packageIds.forEach((value) => params.append("package_id", value));
    categories.forEach((value) => params.append("test_category", value));
    testIds.forEach((value) => params.append("test_id", value));
    try {
      setData(await request(`/hr/analytics?${params.toString()}`, {}, token));
      setError("");
    } catch (loadError) {
      setError(loadError.message);
    }
  }, [token, packageIds, categories, testIds]);

  useEffect(() => { load(); }, [load]);

  // Choosing a category should narrow the test list to that category's tests,
  // rather than leaving hundreds of irrelevant ones in the picker.
  const testOptions = useMemo(
    () => options.tests
      .filter((test) => !categories.length || categories.includes(test.category))
      .map((test) => ({ value: test.id, label: test.name })),
    [options.tests, categories],
  );

  if (!user?.is_hr) {
    return <section className="page-stack" data-testid="hr-analytics-denied"><div className="page-heading"><h1>Not available</h1><p>This dashboard is for HR accounts. Ask your Ivory Health contact if you need access.</p></div></section>;
  }

  const demographics = data?.demographics;
  const utilisation = data?.utilisation;
  const filtered = packageIds.length + categories.length + testIds.length > 0;

  return (
    <section className="page-stack hr-analytics" data-testid="hr-analytics-page">
      <div className="page-heading">
        <p className="eyebrow">Analytics</p>
        <h1>How {tenantName} is using its health benefits.</h1>
        <p>Your organisation only. Nobody is named here — every number is a count across your people.</p>
      </div>

      <div className="hr-filters" data-testid="hr-analytics-filters">
        <MultiSelect label="Package" options={options.packages.map((item) => ({ value: item.id, label: item.name }))} selected={packageIds} onChange={setPackageIds} testId="hr-filter-package" />
        <MultiSelect label="Test category" options={options.test_categories.map((item) => ({ value: item, label: item }))} selected={categories} onChange={setCategories} testId="hr-filter-category" />
        <MultiSelect label="Test name" options={testOptions} selected={testIds} onChange={setTestIds} testId="hr-filter-test" />
        {filtered && <button type="button" className="hr-clear" onClick={() => { setPackageIds([]); setCategories([]); setTestIds([]); }} data-testid="hr-filters-clear">Clear filters</button>}
      </div>

      {error && <p className="form-error" role="alert" data-testid="hr-analytics-error">{error}</p>}

      <div className="section-heading"><div><p className="eyebrow"><Users size={14} /> People</p><h2>Who is covered</h2></div></div>
      <div className="hr-grid">
        <StatCard title="Total employees" value={demographics?.total_employees ?? "—"} hint="Eligible on the roster" testId="hr-total-employees" />
        <StatCard title="Total dependents" value={demographics?.total_dependents ?? "—"} hint="Family profiles added" testId="hr-total-dependents" />
        <AgeCard title="Employee age distribution" distribution={demographics?.employee_age_distribution} testId="hr-employee-age" />
        <DonutCard title="Employee gender" distribution={demographics?.employee_gender_distribution} colors={CATEGORICAL} total={demographics?.employee_gender_distribution?.known ?? 0} testId="hr-employee-gender" />
      </div>

      <div className="section-heading"><div><p className="eyebrow"><BarChart3 size={14} /> Uptake</p><h2>Health check utilisation</h2></div></div>
      <div className="hr-grid">
        <DonutCard title="Employees" distribution={{ rows: [{ label: "Utilised", count: utilisation?.employees.utilised ?? 0, percentage: utilisation?.employees.utilised_percentage ?? 0 }, { label: "Not utilised", count: utilisation?.employees.not_utilised ?? 0, percentage: utilisation?.employees.not_utilised_percentage ?? 0 }] }} colors={[GREEN, AMBER]} total={utilisation?.employees.total ?? 0} testId="hr-employee-utilisation" />
        <DonutCard title="Dependents" distribution={{ rows: [{ label: "Utilised", count: utilisation?.dependents.utilised ?? 0, percentage: utilisation?.dependents.utilised_percentage ?? 0 }, { label: "Not utilised", count: utilisation?.dependents.not_utilised ?? 0, percentage: utilisation?.dependents.not_utilised_percentage ?? 0 }] }} colors={[GREEN, AMBER]} total={utilisation?.dependents.total ?? 0} testId="hr-dependent-utilisation" />
        <article className="hr-card hr-card--wide" data-testid="hr-by-package">
          <p className="hr-card__title">Check-ups by package</p>
          {utilisation?.by_package?.length ? (
            <ResponsiveContainer width="100%" height={Math.max(160, utilisation.by_package.length * 42)}>
              <BarChart layout="vertical" data={utilisation.by_package} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
                <XAxis type="number" allowDecimals={false} tickLine={false} axisLine={false} fontSize={12} />
                <YAxis type="category" dataKey="package_name" width={150} tickLine={false} axisLine={false} fontSize={12} />
                <Tooltip />
                <Legend />
                <Bar dataKey="completed" name="Completed" stackId="a" fill={GREEN} radius={[0, 0, 0, 0]} />
                <Bar dataKey="upcoming" name="Upcoming" stackId="a" fill={SLATE} radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="hr-empty">{filtered ? "No check-ups match these filters." : "No check-ups have been arranged yet."}</p>}
        </article>
      </div>

      <div className="section-heading"><div><p className="eyebrow"><Users size={14} /> Family</p><h2>Who dependents are</h2></div></div>
      <div className="hr-grid">
        <AgeCard title="Dependent age distribution" distribution={demographics?.dependent_age_distribution} testId="hr-dependent-age" />
        <DonutCard title="Dependent gender" distribution={demographics?.dependent_gender_distribution} colors={CATEGORICAL} total={demographics?.dependent_gender_distribution?.known ?? 0} testId="hr-dependent-gender" />
        <DonutCard title="Dependent relationship" distribution={demographics?.dependent_relationship_distribution} colors={CATEGORICAL} total={demographics?.dependent_relationship_distribution?.known ?? 0} testId="hr-dependent-relationship" />
      </div>

      {/* Stated rather than silently absent: the reference dashboard this was
          modelled on carries test-result charts, and their absence here is a
          missing data feed, not an oversight. */}
      {data?.test_results?.available === false && (
        <p className="hr-note" data-testid="hr-test-results-note"><Info size={16} />{data.test_results.reason}</p>
      )}
    </section>
  );
}
